/**
 * D6 内容画像看板（Phase 5-4 / Design §5.2 D6）
 *
 * KPI: 书数 / 章节数 / 定稿率
 * 切片: 日期范围 + 风格
 * 图表: 风格分布饼图 + 月份新书折线
 * 导出: CSV
 *
 * V1 取舍: users 表无 birth_year —— 「用户年龄段切片」目前用「作品风格」近似替代；
 * birth_year 加表后 V1.1 切换。
 */
import { useState } from 'react';
import { Card, Row, Col, Statistic, Space, Result, Input, Alert } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { PermissionGate } from '../../components/PermissionGate';
import { ChartContainer } from '../../components/dashboard/ChartContainer';
import { TimeSlicer, type TimeSlicerValue } from '../../components/dashboard/TimeSlicer';
import { ExportButton } from '../../components/dashboard/ExportButton';
import { PERMISSIONS } from '../../contracts/permissions';
import { getContentProfile, type ContentProfileFilter } from '../../api/dashboard';

const DEFAULT_RANGE_DAYS = 180;
function defaultRange(): { startAt: string; endAt: string } {
  const end = new Date();
  const start = new Date(end.getTime() - DEFAULT_RANGE_DAYS * 24 * 3600 * 1000);
  return { startAt: start.toISOString(), endAt: end.toISOString() };
}

function ContentInner() {
  const initial = defaultRange();
  const [slicer, setSlicer] = useState<TimeSlicerValue>({
    startAt: initial.startAt,
    endAt: initial.endAt,
  });
  const [style, setStyle] = useState<string>('');

  const filter: ContentProfileFilter = {
    startAt: slicer.startAt,
    endAt: slicer.endAt,
    style: style || undefined,
  };

  const query = useQuery({
    queryKey: ['dashboard', 'content', filter],
    queryFn: () => getContentProfile(filter),
  });

  const data = query.data;

  // 风格分布饼图
  const styleOption = data
    ? {
        tooltip: { trigger: 'item' as const },
        legend: { type: 'scroll' as const, orient: 'horizontal' as const, bottom: 0 },
        series: [
          {
            name: '风格分布',
            type: 'pie' as const,
            radius: ['40%', '70%'],
            data: data.styleDistribution.map((s) => ({
              name: s.style,
              value: s.workCount,
            })),
          },
        ],
      }
    : {};

  // 月份新书折线
  const monthOption = data
    ? {
        tooltip: { trigger: 'axis' as const },
        xAxis: {
          type: 'category' as const,
          data: data.monthlyWorksSeries.map((m) => m.bucket),
        },
        yAxis: { type: 'value' as const, name: '新作品数' },
        series: [
          {
            name: '新作品',
            type: 'bar' as const,
            data: data.monthlyWorksSeries.map((m) => m.workCount),
          },
        ],
      }
    : {};

  return (
    <div data-testid="dashboard-content">
      <Card
        title="D6 内容画像看板"
        extra={<ExportButton kind="content" filter={filter} />}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Space wrap>
            <TimeSlicer value={slicer} onChange={setSlicer} showPeriod={false} />
            <Input
              placeholder="作品风格（如 classic）"
              allowClear
              value={style}
              onChange={(e) => setStyle(e.target.value)}
              style={{ width: 200 }}
              data-testid="content-style"
            />
          </Space>

          {data?.notes && <Alert type="info" message={data.notes} showIcon />}

          <Row gutter={16}>
            <Col span={8}>
              <Card>
                <Statistic
                  title="作品数"
                  value={data?.workCount ?? 0}
                  loading={query.isLoading}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="章节总数"
                  value={data?.chapterCount ?? 0}
                  loading={query.isLoading}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="定稿率"
                  value={data ? data.finalizationRate * 100 : 0}
                  precision={2}
                  suffix="%"
                  loading={query.isLoading}
                />
              </Card>
            </Col>
          </Row>

          <ChartContainer
            title="风格分布"
            option={styleOption}
            loading={query.isLoading}
            empty={!data || data.styleDistribution.length === 0}
            testId="content-style-chart"
          />
          <ChartContainer
            title="月份新作品数"
            option={monthOption}
            loading={query.isLoading}
            empty={!data || data.monthlyWorksSeries.length === 0}
            testId="content-month"
          />
        </Space>
      </Card>
    </div>
  );
}

export default function ContentDashboardPage() {
  return (
    <PermissionGate
      point={PERMISSIONS.DASHBOARD_READ}
      fallback={
        <Result
          status="403"
          title="无权访问"
          subTitle="数据看板需 dashboard:read 权限。"
        />
      }
    >
      <ContentInner />
    </PermissionGate>
  );
}
